import React, { useCallback, useEffect, useState } from 'react';
import { Head, router } from '@inertiajs/react';
import { notifyTimerChanged } from '../components/tracker/Timer/TimerBar';
import AiHandoffSheet from '../components/Todo/AiHandoffSheet';
import DetailPane from '../components/Todo/DetailPane';
import AppLayout from '../layouts/AppLayout';
import api, { ApiError } from '../lib/api';
import type { HandoffTarget } from '../types/aiHandoff';
import type { TaskPageProps, TodoTask } from '../types/todo';

type TaskPageComponent = React.FC<TaskPageProps> & {
  layout?: (page: React.ReactNode) => React.ReactNode;
};

// 单任务独立页。内容复用 DetailPane，但容器是页面级的：不带左栏、不带任务
// 列表、不带额度胶囊 —— 将来开公开访问时，这些正是不该给拿到链接的人看的
// 东西，形态现在就得是对的（spec §1.3）。
//
// DetailPane 需要的那批回调是照 Todo.tsx 复制过来的，不做抽象。这是 spec
// §5.3 明写的有意技术债：它目前只有两个调用方，这个事实还没稳定下来。
const TaskPage: TaskPageComponent = ({
  task: taskProp,
  projects,
  categories,
  today,
  running_entry,
  back_href,
}) => {
  // 本地副本供乐观更新用。props 变了（router.reload 之后）跟着重置。
  const [task, setTask] = useState<TodoTask>(taskProp);
  const [capBlocked, setCapBlocked] = useState(false);
  const [handoff, setHandoff] = useState<HandoffTarget | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTask(taskProp);
  }, [taskProp]);

  // 三栏页那份重载四个 prop，这里只有 task 一个
  const refresh = useCallback(() => {
    router.reload({ only: ['task', 'running_entry'] });
  }, []);

  const goBack = useCallback(() => router.visit(back_href), [back_href]);

  const patchTask = useCallback(
    async (id: number, patch: Record<string, unknown>, opts: { capAware?: boolean } = {}) => {
      setError(null);
      try {
        await api.tasks.update(id, patch);
        setCapBlocked(false);
        refresh();
        return true;
      } catch (e) {
        if (opts.capAware && e instanceof ApiError && e.status === 422) {
          setCapBlocked(true);
        } else {
          setError(e instanceof Error ? e.message : '更新失败');
        }
        return false;
      }
    },
    [refresh]
  );

  const patchOptimistic = useCallback(
    async (target: TodoTask, patch: Partial<TodoTask>) => {
      const rollback = Object.fromEntries(
        Object.keys(patch).map((key) => [key, target[key as keyof TodoTask]])
      ) as Partial<TodoTask>;

      setTask((prev) => ({ ...prev, ...patch }));
      setError(null);
      try {
        await api.tasks.update(target.id, patch as Record<string, unknown>);
        refresh();
      } catch (e) {
        setTask((prev) => ({ ...prev, ...rollback }));
        setError(e instanceof Error ? e.message : '更新失败');
      }
    },
    [refresh]
  );

  const toggleComplete = useCallback(
    (target: TodoTask) => patchOptimistic(target, { completed: !target.completed }),
    [patchOptimistic]
  );

  // 删掉之后这个页面就没有内容了，回列表
  const destroyTask = useCallback(
    async (id: number) => {
      setError(null);
      try {
        await api.tasks.delete(id);
        goBack();
      } catch (e) {
        setError(e instanceof Error ? e.message : '删除失败');
      }
    },
    [goBack]
  );

  const createStep = useCallback(
    async (parentId: number, title: string) => {
      setError(null);
      try {
        await api.tasks.create({ title, parent_id: parentId });
        refresh();
      } catch (e) {
        setError(e instanceof Error ? e.message : '添加步骤失败');
      }
    },
    [refresh]
  );

  // 与 Todo.tsx 那两个同形，只是基准 id 是本页这条任务而不是 selectedId。
  // 照这个文件既有的决定各写一份，不做抽象。
  const linkExisting = useCallback(
    async (childId: number) => {
      setError(null);
      try {
        await api.tasks.update(childId, { parent_id: task.id });
        refresh();
      } catch (e) {
        setError(e instanceof Error ? e.message : '挂上失败');
      }
    },
    [task.id, refresh]
  );

  const unlinkParent = useCallback(async () => {
    setError(null);
    try {
      await api.tasks.update(task.id, { parent_id: null });
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : '解绑失败');
    }
  }, [task.id, refresh]);

  const toggleStep = useCallback(
    (stepId: number, completed: boolean) => {
      void patchTask(stepId, { completed });
    },
    [patchTask]
  );

  const renameStep = useCallback(
    (stepId: number, title: string) => {
      void patchTask(stepId, { title });
    },
    [patchTask]
  );

  const deleteStep = useCallback(
    async (stepId: number) => {
      setError(null);
      try {
        await api.tasks.delete(stepId);
        refresh();
      } catch (e) {
        setError(e instanceof Error ? e.message : '删除步骤失败');
      }
    },
    [refresh]
  );

  const startTimer = useCallback(
    async (taskId: number) => {
      setError(null);
      try {
        await api.timeEntries.start({ task_id: taskId });
        notifyTimerChanged();
        refresh();
      } catch (e) {
        setError(e instanceof Error ? e.message : '开始计时失败');
      }
    },
    [refresh]
  );

  const stopTimer = useCallback(async () => {
    setError(null);
    try {
      await api.timeEntries.stop();
      notifyTimerChanged();
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : '停止失败');
    }
  }, [refresh]);

  return (
    <>
      <Head title={task.title} />
      <div className="mx-auto max-w-3xl">
        <a
          href={back_href}
          className="mb-3 inline-block text-sm text-todo-muted hover:text-todo-ink"
        >
          ← 返回列表
        </a>
        <div className="overflow-hidden rounded-lg border border-todo-line bg-todo-card">
          <DetailPane
            task={task}
            projects={projects}
            categories={categories}
            today={today}
            capBlocked={capBlocked}
            error={error}
            runningEntry={running_entry}
            onClose={goBack}
            onToggle={toggleComplete}
            onPatch={patchTask}
            onOptimistic={patchOptimistic}
            onDestroy={destroyTask}
            onRefresh={refresh}
            onCreateStep={createStep}
            onLinkExisting={linkExisting}
            onUnlinkParent={unlinkParent}
            onToggleStep={toggleStep}
            onRenameStep={renameStep}
            onDeleteStep={deleteStep}
            onStartTimer={startTimer}
            onStopTimer={stopTimer}
            onHandoff={(t) => setHandoff({ kind: 'task', task: t })}
          />
        </div>
      </div>
      <AiHandoffSheet target={handoff} onClose={() => setHandoff(null)} />
    </>
  );
};

TaskPage.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>;

export default TaskPage;
